import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClientDetails,
  CreateClientRequest,
  CreateDeliveryAddressRequest,
  ListClientsQuery,
  UpdateClientRequest,
  UpdateDeliveryAddressRequest,
} from '@kreart/shared';
import { clientsApi } from './api';

const ROOT = ['clients'] as const;
export const clientsQueryKeys = {
  all: ROOT,
  list: (q: ListClientsQuery) => [...ROOT, 'list', q] as const,
  detail: (id: string) => [...ROOT, 'detail', id] as const,
};

export function useClients(query: ListClientsQuery) {
  return useQuery({
    queryKey: clientsQueryKeys.list(query),
    queryFn: () => clientsApi.list(query),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: id ? clientsQueryKeys.detail(id) : ['clients', 'detail', 'nil'],
    queryFn: () => clientsApi.get(id!),
    enabled: Boolean(id),
    meta: { silent: true },
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateClientRequest) => clientsApi.create(body),
    meta: { silent: true },
    onSuccess: (data: ClientDetails) => {
      qc.setQueryData(clientsQueryKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useUpdateClient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateClientRequest) => clientsApi.update(id, body),
    meta: { silent: true },
    onSuccess: (data: ClientDetails) => {
      qc.setQueryData(clientsQueryKeys.detail(id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useDeleteClient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clientsApi.remove(id),
    meta: { silent: true },
    onSuccess: () => {
      qc.removeQueries({ queryKey: clientsQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useCreateAddress(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDeliveryAddressRequest) => clientsApi.createAddress(clientId, body),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsQueryKeys.detail(clientId) });
    },
  });
}

export function useUpdateAddress(clientId: string, addressId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDeliveryAddressRequest) =>
      clientsApi.updateAddress(clientId, addressId, body),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsQueryKeys.detail(clientId) });
    },
  });
}

export function useDeleteAddress(clientId: string, addressId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clientsApi.removeAddress(clientId, addressId),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsQueryKeys.detail(clientId) });
    },
  });
}
